import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, throwError } from 'rxjs';
import { EcpAuthService } from './ecp-auth.service';
import { TextEncoder } from 'text-encoding';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const ecpAuthService = inject(EcpAuthService);
    const translocoService = inject(TranslocoService);

    // Clone the request object with Accept-Language header (preserve any pre-set value)
    let newReq = req.headers.has('Accept-Language')
        ? req.clone()
        : req.clone({
              headers: req.headers.set('Accept-Language', translocoService.getActiveLang().toUpperCase()),
          });

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        newReq = req.clone({
            headers: newReq.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
        });
    }

    if (authService.telegramAuth) {
        const telegramAuth = JSON.parse(authService.telegramAuth);
        //create query string from telegramAuth object
        let queryString = Object.keys(telegramAuth)
            .map((key) => key + '=' + telegramAuth[key])
            .join('&');

        const textEncoder = new TextEncoder();
        const encoded = textEncoder.encode(queryString);

        newReq = newReq.clone({
            headers: newReq.headers.set(
                'x-telegram-auth',
                btoa(String.fromCharCode(...encoded))
            ),
        });
    }
    //-----------------------------------
    // api/docs/${tin}/token
    // api/docs/tms-draft-creation
    const docsEndpoint = 'api/docs/';
    if (newReq.url.includes(docsEndpoint)) {
        newReq = newReq.clone({
            headers: newReq.headers.set(
                'docs-user-key',
                ecpAuthService.docsUserKey
            ),
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (
                error instanceof HttpErrorResponse &&
                error.status === 401 &&
                !newReq.url.includes('api/auth/') &&
                !newReq.url.includes('api/register') &&
                !router.url.startsWith('/sign-in')
            ) {
                authService.signOut();
                router.navigateByUrl(`sign-in?redirectURL=${router.url}`);
            }

            return throwError(error);
        })
    );
};
